import {
  ERelationToSeg,
  EClosedPathRelation,
  IShapePoint2d,
  IShapeSeg2d,
  EPointAndClosedRelation
} from '@/editor/types/shape';
import { ArcSeg2d } from '../arcSeg2d';
import { IPoint2d } from '../IPoint2d';
import { LineSeg2d } from '../LineSeg2d';
import { segRelations } from './relation';
import { pointOnSeg } from './other';
import { RingChecker } from './ringChecker';
import { isArc } from '@/editor/utils/arc';

function buildSegsFromPath(points: IShapePoint2d[]): IShapeSeg2d[] {
  const segs: IShapeSeg2d[] = [];
  const n = points.length;
  for (let i = 0; i < n; i++) {
    const p1 = points[i];
    const p2 = points[(i + 1) % n];
    if (isArc(p1)) {
      segs.push(new ArcSeg2d({ start: p1, end: p2, center: p1.center, clockwise: p1.clockwise }));
    } else {
      segs.push(new LineSeg2d(p1, p2));
    }
  }
  return segs;
}

function ringHasArc(segments: IShapeSeg2d[]) {
  for (const segment of segments) {
    if (segment instanceof ArcSeg2d) {
      return true;
    }
  }

  return false;
}

// 射线法：从 point 向 +x 方向发射射线，统计与路径的交叉次数
function countRayCrossings(point: IPoint2d, segments: IShapeSeg2d[]): number {
  let count = 0;
  const py = point.y;

  for (const seg of segments) {
    if (seg instanceof LineSeg2d) {
      // if (seg.isHorizontalSeg()) continue;
      const y1 = seg.start.y;
      const y2 = seg.end.y;
      // 线段必须跨过 py（一端在上，一端在下） // TODO: 待完善，需要考虑穿过seg端点的情况
      if (y1 > py === y2 > py) continue;

      const t = (py - y1) / (y2 - y1);
      const x = seg.start.x + t * (seg.end.x - seg.start.x);
      if (x > point.x) count++;
    } else if (seg instanceof ArcSeg2d) {
      const { center, radius } = seg;
      const dy = py - center.y;
      const disc = radius * radius - dy * dy;
      if (disc < 0) continue;
      const sqrtDisc = Math.sqrt(disc);
      const candidates: IPoint2d[] = [
        { x: center.x + sqrtDisc, y: py },
        { x: center.x - sqrtDisc, y: py }
      ];
      for (const pt of candidates) {
        if (pt.x <= point.x) continue;
        if (seg.pointOnSegment(pt)) count++;
      }
    }
  }

  return count;
}

// 边长（直线=弦长，圆弧=半径×弧度）
function segLength(seg: IShapeSeg2d): number {
  if (seg instanceof ArcSeg2d) return seg.radius * seg.getSweepAngle();
  return Math.hypot(seg.end.x - seg.start.x, seg.end.y - seg.start.y);
}

// 取「显著边」的中点用于包含判定：忽略长度不足总周长 1% 的极短边（如墙体轮廓角点处的封口小边），
// 它们的中点贴近角点、易因壁厚偏移虚假越界。若全部边都极短（退化）则退回使用全部中点。
function significantMidPoints(segs: IShapeSeg2d[]): IPoint2d[] {
  const perimeter = segs.reduce((sum, seg) => sum + segLength(seg), 0);
  const minLen = perimeter * 0.01;
  const significant = segs.filter((seg) => segLength(seg) >= minLen);
  return (significant.length > 0 ? significant : segs).map((seg) => getMidPoint(seg));
}

// 计算两个闭合路径的包含关系
// EClosedPathRelation: NoRelation-无关系，Overlap-完全重叠，Inside-shapeA在shapeB内部，Outside-shapeA在shapeB外部，Intersect-相交
export function calculateShapeRelation(
  shapeA: IShapePoint2d[],
  shapeB: IShapePoint2d[],
  isSeparateOutline: boolean = false
): EClosedPathRelation {
  const segsA = buildSegsFromPath(shapeA);
  const segsB = buildSegsFromPath(shapeB);

  // 优先判断完全重叠：两环边界互相覆盖——A 的每个显著边中点都落在 B 边界上，且 B 的每个显著边中点都落在 A 边界上。
  // 不要求段数相同：当共享边被额外的共线顶点切分时（如壁芯左边在 y 中点处分段、轮廓左边在壁厚偏移处分段），
  // 两环段数会不同，但仍是同一形状。旧的「段数相同 + 整条 Same/Opposite」判据会漏判这种情况 → 误落到 NoRelation。
  const midsA = significantMidPoints(segsA);
  const midsB = significantMidPoints(segsB);
  const aAllOnB = midsA.length > 0 && midsA.every((p) => segsB.some((seg) => pointOnSeg(seg, p)));
  const bAllOnA = midsB.length > 0 && midsB.every((p) => segsA.some((seg) => pointOnSeg(seg, p)));
  if (aAllOnB && bAllOnA) return EClosedPathRelation.Overlap;

  // 检测路径是否真正穿越（顶点落在对方线段上属于边界接触，不代表路径穿越）
  if (isSeparateOutline) {
    for (const segA of segsA) {
      for (const segB of segsB) {
        const rels = segRelations(segA, segB);
        if (rels === ERelationToSeg.IntersectInternal) return EClosedPathRelation.Intersect;
      }
    }
  } else {
    // 检查是否轮廓真正穿越。共用整条边（Same/Opposite）只是边界贴合（如外墙边界环与房间轮廓共用墙体中心线、
    // 相邻房间共用一道墙），并不代表轮廓穿越——若据此判为相交，会切断真实的嵌套包含关系（外边界⊃房间）、
    // 导致后续射线包含判定被跳过，开洞/区域归属错乱。仅当存在真实内部交叉点时才判为相交。
    for (const segA of segsA) {
      for (const segB of segsB) {
        const rels = segRelations(segA, segB);
        const relArr = Array.isArray(rels) ? rels : [rels];
        if (relArr.includes(ERelationToSeg.IntersectInternal)) {
          return EClosedPathRelation.Intersect;
        }
      }
    }
  }

  // 无边界穿越，用射线法判断包含关系。
  // 取中点时：① 过滤落在对方边界上的中点（共享边会导致射线法不稳定）；
  // ② 忽略相对极短的边（如墙体轮廓在角点处的封口小边）——它们的中点贴近角点、易因壁厚偏移而虚假戳出，
  //    会让"全部中点在内"误判失败。真包含时所有中点都在内、过滤不改变结论；仅当短边中点虚假越界时才被忽略。
  const nonBoundaryAPoints = midsA.filter((p) => !segsB.some((seg) => pointOnSeg(seg, p)));
  if (nonBoundaryAPoints.length > 0) {
    const allInB = ringHasArc(segsB)
      ? nonBoundaryAPoints.every((p) => RingChecker.isPointInRing(p, segsB))
      : nonBoundaryAPoints.every((p) => countRayCrossings(p, segsB) % 2 === 1);
    if (allInB) return EClosedPathRelation.Inside;
  }

  const nonBoundaryBPoints = midsB.filter((p) => !segsA.some((seg) => pointOnSeg(seg, p)));
  if (nonBoundaryBPoints.length > 0) {
    const allInA = ringHasArc(segsA)
      ? nonBoundaryBPoints.every((p) => RingChecker.isPointInRing(p, segsA))
      : nonBoundaryBPoints.every((p) => countRayCrossings(p, segsA) % 2 === 1);
    if (allInA) return EClosedPathRelation.Outside;
  }

  return EClosedPathRelation.NoRelation;
}

export function getPointAndClosedRelation(point: IPoint2d, path: IShapePoint2d[]): EPointAndClosedRelation {
  const segs = buildSegsFromPath(path);
  if (segs.some((seg) => pointOnSeg(seg, point))) return EPointAndClosedRelation.On;
  if (ringHasArc(segs)) {
    if (RingChecker.isPointInRing(point, segs)) return EPointAndClosedRelation.Inside;
  } else {
    if (countRayCrossings(point, segs) % 2 === 1) return EPointAndClosedRelation.Inside;
  }
  return EPointAndClosedRelation.Outside;
}

export function getMidPoint(seg: IShapeSeg2d): IPoint2d {
  if (seg instanceof ArcSeg2d) {
    const midAngle = seg.startAngle + (seg.getSweepAngle() / 2) * (seg.clockwise ? -1 : 1);
    return {
      x: seg.center.x + seg.radius * Math.cos(midAngle),
      y: seg.center.y + seg.radius * Math.sin(midAngle)
    };
  } else if (seg instanceof LineSeg2d) {
    return {
      x: (seg.start.x + seg.end.x) / 2,
      y: (seg.start.y + seg.end.y) / 2
    };
  }

  return { x: 0, y: 0 }; // TODO: 待完善
}
